// ./src/components/generic/form/SelectField.tsx
//
// Select wrapper on the shared `(name, value)` change contract. Emits the
// selected value (or null when cleared).
//
// ⚠ ON A PHONE IT IS ALWAYS THE FULL-SCREEN PICKER (OptionPickerModal), whatever the list's length (owner, 2026-10-09:
//   "on mobile we use full screen selectors consistently"). It used to keep Mantine's inline dropdown for lists of 7 or
//   fewer, and inside a bottom-sheet drawer that dropdown resized the sheet as it opened and as the keyboard came up:
//   the marketing-channel picker flickered and the block editor's font picker could not be used. A short list opens
//   full screen WITHOUT a search box (unless the caller asked for `searchable`). Desktop is always the dropdown.
//   There is no opt-out: a raw Mantine Select/MultiSelect/Autocomplete is refused by `check-mobile-pickers`.
//
// It also owns the app's ONE "create it right here" affordance: pass
// `createLabel` + `onCreate` and the action renders FIRST, in the primary colour,
// with a + icon — in the dropdown AND in the full-screen picker. Never hand-roll
// a sentinel option; that's how we ended up with the action at the bottom in one
// picker and the top in another.

import React, { useMemo, useRef, useState } from 'react';
import { Group, Select, SelectProps, Text, TextInput } from '@mantine/core';

import { useAppEnvironment } from '../AppEnvironmentContext';
import { AppIcon } from '../AppIcon';

import { FieldChangeHandler } from './types';
import { OptionPickerModal } from './OptionPickerModal';
import { phonePickerSearchable, usesPhonePicker } from './pickerRules';

// Reserved option value for the "create in place" row (dropdown branch only).
const CREATE_VALUE = '__select_field_create__';

export interface SelectFieldProps extends Omit<SelectProps, 'onChange' | 'value'> {
  name?: string;
  value?: string | null;
  onChange?: FieldChangeHandler<string | null>;
  /** Label of the pinned "create in place" action (without a leading +). */
  createLabel?: string;
  /** Called when the create action is picked, with whatever the user had typed. */
  onCreate?: (search: string) => void;
  /** Spinner in the mobile picker's search field (a server-side lookup is in flight). */
  searchLoading?: boolean;
  /**
   * The phone picker's surface style (it portals to document.body, outside a themed page) — the public event site
   * passes its theme (`usePortalSurfaceStyle`).
   */
  modalContentStyle?: React.CSSProperties;
}

const countOptions = (data: unknown): number => {
  if (!Array.isArray(data)) {
    return 0;
  }

  let n = 0;
  for (const entry of data) {
    if (entry && typeof entry === 'object' && 'items' in (entry as object)) {
      n += ((entry as { items?: unknown[] }).items ?? []).length;
    } else {
      n += 1;
    }
  }

  return n;
};

const findLabel = (data: unknown, value?: string | null): string => {
  if (value == null || !Array.isArray(data)) {
    return '';
  }

  const scan = (items: unknown[]): string | null => {
    for (const it of items) {
      if (typeof it === 'string' || typeof it === 'number') {
        if (String(it) === value) {
          return String(it);
        }
      } else if (it && typeof it === 'object') {
        const o = it as { value?: unknown; label?: unknown; items?: unknown[] };
        if (o.items) {
          const hit = scan(o.items);
          if (hit != null) {
            return hit;
          }
        } else if (o.value != null && String(o.value) === value) {
          return String(o.label ?? o.value);
        }
      }
    }
    return null;
  };

  return scan(data) ?? '';
};

/**
 * The phone picker's TRIGGER: a read-only input that looks like the dropdown's own box and opens the full-screen picker.
 * It keeps every layout prop the caller gave the select (`mt`, `w`, `flex`, `style`, …) — dropping them moved fields
 * about on a phone — and leaves out only the dropdown's own props.
 */
export const PhonePickerTrigger = React.forwardRef<
  HTMLInputElement,
  Omit<SelectProps, 'onChange' | 'value'> & { displayValue: string; onOpen: () => void; children?: React.ReactNode }
>(({
  displayValue,
  onOpen,
  children,
  // The dropdown's own props — not an input's.
  data: _data,
  defaultValue: _defaultValue,
  onClear: _onClear,
  searchable: _searchable,
  withCheckIcon: _withCheckIcon,
  withAlignedLabels: _withAlignedLabels,
  checkIconPosition: _checkIconPosition,
  nothingFoundMessage: _nothingFoundMessage,
  searchValue: _searchValue,
  defaultSearchValue: _defaultSearchValue,
  onSearchChange: _onSearchChange,
  allowDeselect: _allowDeselect,
  clearable: _clearable,
  clearButtonProps: _clearButtonProps,
  hiddenInputProps: _hiddenInputProps,
  renderOption: _renderOption,
  scrollAreaProps: _scrollAreaProps,
  chevronColor: _chevronColor,
  autoSelectOnBlur: _autoSelectOnBlur,
  openOnFocus: _openOnFocus,
  dropdownOpened: _dropdownOpened,
  defaultDropdownOpened: _defaultDropdownOpened,
  onDropdownOpen: _onDropdownOpen,
  onDropdownClose: _onDropdownClose,
  selectFirstOptionOnChange: _selectFirstOptionOnChange,
  selectFirstOptionOnDropdownOpen: _selectFirstOptionOnDropdownOpen,
  onOptionSubmit: _onOptionSubmit,
  comboboxProps: _comboboxProps,
  filter: _filter,
  limit: _limit,
  withScrollArea: _withScrollArea,
  maxDropdownHeight: _maxDropdownHeight,
  // A Select's vars resolver is typed for a Select, not an input.
  vars: _vars,
  classNames,
  styles,
  rightSection,
  ...inputProps
}, ref) => {
  // Only object styles can be merged onto the trigger (a styles *function* is
  // Select-specific — ignore it rather than mis-applying it to a TextInput).
  const callerStyles: Record<string, React.CSSProperties> =
    styles && typeof styles === 'object' ? (styles as Record<string, React.CSSProperties>) : {};

  return (
    <>
      <TextInput
        {...inputProps}
        // Only object classNames carry over (a function is typed for a Select), as with `styles` below.
        classNames={classNames && typeof classNames === 'object' ? (classNames as Record<string, string>) : undefined}
        ref={ref}
        value={displayValue}
        readOnly
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            onOpen();
          }
        }}
        rightSection={rightSection ?? <AppIcon iconName="ChevronDown" size={18} />}
        rightSectionPointerEvents={rightSection ? inputProps.rightSectionPointerEvents : 'none'}
        styles={{
          ...callerStyles,
          input: { ...(callerStyles.input ?? {}), cursor: 'pointer', caretColor: 'transparent' },
        }}
      />
      {children}
    </>
  );
});

PhonePickerTrigger.displayName = 'PhonePickerTrigger';

// ⓘ It forwards its ref to the input (the dropdown's, or the phone trigger) — a drawer focuses its first field with it.
export const SelectField = React.forwardRef<HTMLInputElement, SelectFieldProps>(({
  name,
  value,
  onChange,
  createLabel,
  onCreate,
  searchLoading,
  modalContentStyle,
  ...rest
}, ref) => {
  const { isMobile } = useAppEnvironment();
  const [pickerOpen, setPickerOpen] = useState(false);
  // Mirrors the dropdown's search box so `onCreate` can pre-fill with what the
  // user typed (Mantine doesn't hand it to the change handler).
  const [search, setSearch] = useState('');
  // ⚠ The search is ALWAYS mirrored here when the caller listens to it (#138 review): a caller with `onSearchChange` and
  //   no `searchValue` (the public airport / station search) froze Mantine's box at '' — controlled by our own state,
  //   which only `setSearch` moves — so the lookup never received two characters.
  const mirrorSearch = (s: string) => {
    setSearch(s);
    rest.onSearchChange?.(s);
  };
  const searchValue = rest.searchValue ?? search;

  const optionCount = useMemo(() => countOptions(rest.data), [rest.data]);
  const canCreate = !!createLabel && !!onCreate && !rest.disabled && !rest.readOnly;

  // The picked option's label, kept when a new server search replaces `data` without it (the box went blank on a phone).
  const lastLabel = useRef<{ value: string; label: string } | null>(null);
  const foundLabel = findLabel(rest.data, value);

  if (value != null && foundLabel) {
    lastLabel.current = { value, label: foundLabel };
  }

  const displayValue = foundLabel || (value != null && lastLabel.current?.value === value ? lastLabel.current.label : '');
  // A full-screen sheet needs a title even where the box has none (a select inside a row, named only by aria-label).
  const title = rest.label ?? rest.placeholder ?? (rest as { 'aria-label'?: string })['aria-label'];

  if (usesPhonePicker({ isMobile, disabled: rest.disabled, readOnly: rest.readOnly })) {
    return (
      <PhonePickerTrigger
        {...rest}
        ref={ref}
        displayValue={displayValue}
        onOpen={() => setPickerOpen(true)}
      >
        <OptionPickerModal
          opened={pickerOpen}
          onClose={() => setPickerOpen(false)}
          data={rest.data}
          value={value ?? null}
          onSelect={(v) => onChange?.(name, v)}
          title={title}
          searchPlaceholder={typeof rest.placeholder === 'string' ? rest.placeholder : undefined}
          searchable={phonePickerSearchable({ searchable: rest.searchable, optionCount, canCreate, externalSearch: !!rest.onSearchChange })}
          searchValue={rest.onSearchChange ? searchValue : rest.searchValue}
          onSearchChange={rest.onSearchChange ? mirrorSearch : undefined}
          loading={searchLoading}
          nothingFoundMessage={rest.nothingFoundMessage}
          clearable={rest.clearable}
          // As on a desktop (Mantine's default): tapping the chosen row empties an optional select — a required one keeps it.
          allowDeselect={rest.allowDeselect !== false && !rest.required}
          renderOption={rest.renderOption ? (item) => rest.renderOption?.({ option: item.option as never, checked: item.checked }) : undefined}
          contentStyle={modalContentStyle}
          createLabel={canCreate ? createLabel : undefined}
          onCreate={
            canCreate
              ? (text) => {
                  setPickerOpen(false);
                  onCreate?.(text);
                }
              : undefined
          }
        />
      </PhonePickerTrigger>
    );
  }

  // Dropdown branch: the create action is the FIRST option, styled as an action.
  const data = canCreate
    ? [{ value: CREATE_VALUE, label: createLabel as string }, ...(Array.isArray(rest.data) ? rest.data : [])]
    : rest.data;

  const renderOption: SelectProps['renderOption'] = (item) => {
    if (item.option.value === CREATE_VALUE) {
      return (
        <Group gap="xs" wrap="nowrap">
          <AppIcon iconName="Plus" size={16} color="var(--mantine-color-brand-6)" />
          <Text size="md" c="brand.7" fw={600}>
            {item.option.label}
          </Text>
        </Group>
      );
    }

    return rest.renderOption ? rest.renderOption(item) : item.option.label;
  };

  return (
    <Select
      {...rest}
      ref={ref}
      data={data}
      value={value ?? null}
      searchValue={rest.searchable || rest.onSearchChange ? searchValue : rest.searchValue}
      onSearchChange={rest.searchable || rest.onSearchChange ? mirrorSearch : undefined}
      renderOption={canCreate || rest.renderOption ? renderOption : undefined}
      onChange={(v) => {
        if (v === CREATE_VALUE) {
          onCreate?.(search.trim());
          return; // keep the current selection — the create flow supplies the new one
        }

        onChange?.(name, v);
      }}
    />
  );
});

SelectField.displayName = 'SelectField';
